import { useState, useEffect } from 'react';
import React from 'react';
import { AEA_PLACE, AEA_PLACE_ORDER, AeaConsentBanner, AeaExposureMap, AeaTrialUpload,
         AeaHowTo, AeaRegisterDeviceDialog, AeaBrokerRequestDialog, AeaIngestResponseDialog,
         RATINGS, api } from './_barrel.js';


        function ExposurePanel({ subjectId, subjectName, token, me }) {
            const [devices, setDevices] = useState(null);
            const [sel, setSel] = useState(null);
            const [data, setData] = useState(null);
            const [points, setPoints] = useState([]);
            const [err, setErr] = useState(null);
            const [busyPlace, setBusyPlace] = useState(null);
            // Which flow dialog is open: 'register' | 'dsar' | null; plus the
            // broker request being answered when uploading a response.
            const [dialog, setDialog] = useState(null);
            const [ingestReq, setIngestReq] = useState(null);
            const canWrite = ['admin', 'lead_analyst', 'analyst'].includes(me?.analyst?.role);
            const name = subjectName || (data && data.consent && data.consent.principal_name);

            const loadDevices = React.useCallback(() =>
                api(`/api/v1/aea/subjects/${subjectId}/devices`, {}, token)
                    .then(r => { const ds = r.devices || []; setDevices(ds); return ds; }),
                [subjectId, token]);

            useEffect(() => {
                let live = true;
                loadDevices()
                    .then(ds => { if (live && ds.length) setSel(ds[0].id); })
                    .catch(e => { if (live) setErr((e.data && e.data.detail) || e.message); });
                return () => { live = false; };
            }, [loadDevices]);

            function handleTrialDone(deviceId) {
                setErr(null);
                loadDevices().then(() => setSel(deviceId)).catch(() => {});
            }

            const loadExposure = React.useCallback((deviceId) => {
                setData(null); setPoints([]);
                return Promise.all([
                    api(`/api/v1/aea/devices/${deviceId}/exposure`, {}, token),
                    api(`/api/v1/aea/devices/${deviceId}/points?limit=4000`, {}, token).catch(() => ({ points: [] })),
                ]).then(([ex, pt]) => { setData(ex); setPoints(pt.points || []); });
            }, [token]);

            useEffect(() => {
                if (!sel) return; let live = true;
                loadExposure(sel).catch(e => { if (live) setErr((e.data && e.data.detail) || e.message); });
                return () => { live = false; };
            }, [sel, loadExposure]);

            function reloadCurrent() {
                if (sel) loadExposure(sel).catch(e => setErr((e.data && e.data.detail) || e.message));
            }

            async function classify(placeId, place_type) {
                setBusyPlace(placeId);
                try {
                    await api(`/api/v1/aea/places/${placeId}/classify`,
                              { method: 'POST', body: JSON.stringify({ place_type, analyst_confirmed: true }) }, token);
                    await loadExposure(sel);
                } catch (e) { setErr((e.data && e.data.detail) || e.message); }
                finally { setBusyPlace(null); }
            }

            // Dialogs are rendered once and driven by the `dialog`/`ingestReq` state,
            // so they are available in both the empty and populated states.
            const dialogs = (
                <>
                    {dialog === 'register' && (
                        <AeaRegisterDeviceDialog
                            subjectId={subjectId} subjectName={name} token={token}
                            onClose={() => setDialog(null)}
                            onDone={(deviceId) => { setDialog(null); handleTrialDone(deviceId); }} />
                    )}
                    {dialog === 'dsar' && sel && (
                        <AeaBrokerRequestDialog
                            deviceId={sel} subjectName={name} token={token}
                            onClose={() => setDialog(null)}
                            onDone={() => { setDialog(null); reloadCurrent(); }} />
                    )}
                    {ingestReq && (
                        <AeaIngestResponseDialog
                            deviceId={sel} brokerRequest={ingestReq} token={token}
                            onClose={() => setIngestReq(null)}
                            onDone={() => { setIngestReq(null); reloadCurrent(); }} />
                    )}
                </>
            );

            if (err) return <div className="panel p-6 text-sm" style={{ color: 'var(--red)' }}>Could not load exposure: {err}</div>;
            if (devices === null) return <div className="panel p-6 text-muted text-sm">Loading exposure…</div>;

            if (devices.length === 0) return (
                <div>
                    {canWrite && <AeaHowTo defaultOpen />}
                    <div className="panel p-8">
                        {canWrite
                            ? <div className="text-center">
                                <AeaTrialUpload subjectId={subjectId} token={token} onDone={handleTrialDone} />
                                <div className="flex items-center gap-3 my-6" style={{ maxWidth: 360, margin: '24px auto' }}>
                                    <div className="h-px flex-1" style={{ background: 'var(--line)' }} />
                                    <span className="font-mono text-xs uppercase tracking-widest text-dim">or the real broker data</span>
                                    <div className="h-px flex-1" style={{ background: 'var(--line)' }} />
                                </div>
                                <button className="btn-gold" style={{ background: 'transparent', color: 'var(--gold)', border: '1px solid var(--gold-dim)' }}
                                        onClick={() => setDialog('register')}>
                                    Register device by advertising ID (MAID)
                                </button>
                                <div className="text-xs text-muted mt-3" style={{ maxWidth: 460, margin: '12px auto 0' }}>
                                    Record the subject's consent, add their device, then send DSAR letters to brokers —
                                    the tab tracks the deadlines and grades the data as it comes back.
                                </div>
                            </div>
                            : <div className="text-center">
                                <div className="font-display text-xl gold mb-2">No adtech location exposure on file</div>
                                <div className="text-sm text-muted" style={{ maxWidth: 520, margin: '0 auto' }}>
                                    AEA is consent-gated: a device appears here once consent is recorded and a
                                    location export (a broker DSAR return, or the subject's own history) has been
                                    ingested. None has been registered for this subject yet.
                                </div>
                            </div>}
                    </div>
                    {dialogs}
                </div>
            );

            const s = data && data.summary;
            const grade = s && s.grade;
            const gradeHex = (s && s.hex) || (grade ? RATINGS.gradeHex(grade) : 'var(--muted)');
            const pred = s && s.predictability_score;
            const selDevice = devices.find(d => d.id === sel);
            const noData = selDevice && (selDevice.point_count || 0) === 0;

            return (
                <div>
                    {canWrite && <AeaHowTo />}

                    {/* Device switcher + actions */}
                    <div className="flex gap-2 mb-5 flex-wrap items-center">
                        {devices.map(d => (
                            <button key={d.id} onClick={() => setSel(d.id)}
                                    className={`chip ${sel === d.id ? 'active' : ''}`}>
                                {d.label || d.platform} <span className="n">{d.point_count}</span>
                            </button>
                        ))}
                        {canWrite && (
                            <button className="chip" onClick={() => setDialog('register')} title="Register another device by advertising ID">
                                ＋ Device
                            </button>
                        )}
                        <div style={{ flex: 1 }} />
                        {canWrite && sel && data && !data.locked && (
                            <button className="btn-gold" style={{ padding: '6px 14px', fontSize: 13 }}
                                    onClick={() => setDialog('dsar')}>
                                ＋ Log DSAR request
                            </button>
                        )}
                    </div>

                    {data && data.consent && <AeaConsentBanner consent={data.consent} />}

                    {data && data.locked && (
                        <div className="panel p-8 text-center">
                            <div className="font-display text-lg mb-1" style={{ color: 'var(--red)' }}>Exposure locked</div>
                            <div className="text-sm text-muted">Consent has lapsed for this device. Its location
                                data is retained only pending erasure and is not shown.</div>
                        </div>
                    )}

                    {data && !data.locked && noData && (
                        <div className="panel p-8 text-center mb-6">
                            <div className="font-display text-xl gold mb-2">Device registered — no broker data yet</div>
                            <div className="text-sm text-muted mb-5" style={{ maxWidth: 520, margin: '0 auto 20px' }}>
                                Consent is recorded and the device is on file. Next: send DSAR letters to the brokers
                                below, then upload each response as it arrives. The grade appears once data is ingested.
                            </div>
                            {canWrite && (
                                <button className="btn-gold" onClick={() => setDialog('dsar')}>＋ Log a DSAR request</button>
                            )}
                        </div>
                    )}

                    {data && !data.locked && !noData && (
                        <>
                            {/* Grade hero */}
                            <div className="panel mb-6" style={{ overflow: 'hidden' }}>
                                <div className="flex items-stretch">
                                    <div className="flex flex-col items-center justify-center px-8 py-6"
                                         style={{ background: gradeHex + '22', borderRight: '1px solid var(--line)', minWidth: 160 }}>
                                        <div className="font-display" style={{ fontSize: 64, lineHeight: 1, color: gradeHex }}>{grade || '–'}</div>
                                        <div className="font-mono text-xs uppercase tracking-widest mt-2" style={{ color: gradeHex }}>Exposure</div>
                                    </div>
                                    <div className="px-6 py-6 flex-1">
                                        <div className="text-sm mb-4" style={{ color: 'var(--text-soft)' }}>{s && s.headline}</div>
                                        <div className="flex gap-2 mb-4 flex-wrap">
                                            <span className="px-2 py-1 font-mono text-xs uppercase tracking-widest"
                                                  style={{ borderRadius: 'var(--radius-pill)', background: s && s.home_inferred ? 'var(--red-bg)' : 'var(--zone-1)', color: s && s.home_inferred ? 'var(--red)' : 'var(--muted)', border: '1px solid var(--line)' }}>
                                                {s && s.home_inferred ? '● Home inferable' : '○ Home not inferable'}
                                            </span>
                                            <span className="px-2 py-1 font-mono text-xs uppercase tracking-widest"
                                                  style={{ borderRadius: 'var(--radius-pill)', background: s && s.work_inferred ? 'var(--amber-bg)' : 'var(--zone-1)', color: s && s.work_inferred ? 'var(--amber)' : 'var(--muted)', border: '1px solid var(--line)' }}>
                                                {s && s.work_inferred ? '● Work inferable' : '○ Work not inferable'}
                                            </span>
                                        </div>
                                        {/* Predictability gauge */}
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-xs uppercase tracking-widest text-muted" style={{ minWidth: 96 }}>Predictability</span>
                                            <div style={{ flex: 1, height: 8, background: 'var(--zone-1)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--line)' }}>
                                                <div style={{ width: `${Math.min(100, pred || 0)}%`, height: '100%', background: gradeHex }} />
                                            </div>
                                            <span className="font-mono text-sm" style={{ color: gradeHex, minWidth: 54, textAlign: 'right' }}>
                                                {pred == null ? '—' : `${Math.round(pred)}/100`}
                                            </span>
                                        </div>
                                        {s && s.last_analysed_at && (
                                            <div className="text-xs text-muted mt-3">Analysed {new Date(s.last_analysed_at).toLocaleString()}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Map + legend */}
                            <div className="panel mb-6">
                                <div className="panel-header font-display text-xl gold">Exposure map</div>
                                <div className="px-6 py-5">
                                    <AeaExposureMap points={points} places={data.places} />
                                    <div className="flex gap-4 mt-3 flex-wrap">
                                        {AEA_PLACE_ORDER.filter(t => (data.places || []).some(p => p.place_type === t)).map(t => (
                                            <span key={t} className="flex items-center gap-2 text-xs text-muted">
                                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: AEA_PLACE[t].color, display: 'inline-block' }} />
                                                {AEA_PLACE[t].name}
                                            </span>
                                        ))}
                                        <span className="flex items-center gap-2 text-xs text-muted">
                                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--gold)', opacity: 0.4, display: 'inline-block' }} />
                                            Reported position ({points.length.toLocaleString()})
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Why this grade */}
                            {s && s.drivers && s.drivers.length > 0 && (
                                <div className="panel mb-6">
                                    <div className="panel-header font-display text-xl gold">Why this grade</div>
                                    <div className="px-6 py-5">
                                        {s.drivers.map((d, i) => (
                                            <div key={i} className="flex gap-3 mb-3 text-sm" style={{ color: 'var(--text-soft)' }}>
                                                <span style={{ color: gradeHex }}>▸</span><span>{d}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Inferred places + analyst classification */}
                            <div className="panel mb-6">
                                <div className="panel-header font-display text-xl gold">Inferred places</div>
                                {(data.places || []).length === 0
                                    ? <div className="px-6 py-8 text-muted text-sm">No stable places were inferred from this device's positions.</div>
                                    : <div className="px-2 py-2">
                                        {data.places.map(pl => {
                                            const meta = AEA_PLACE[pl.place_type] || AEA_PLACE.transient;
                                            return (
                                                <div key={pl.id} className="table-row px-4 py-3 flex items-center justify-between"
                                                     style={{ borderLeft: `3px solid ${meta.color}` }}>
                                                    <div>
                                                        <div className="text-sm" style={{ color: 'var(--text)' }}>
                                                            {meta.name}
                                                            {pl.analyst_confirmed && <span className="ml-2 font-mono text-xs" style={{ color: 'var(--green)' }}>✓ confirmed</span>}
                                                        </div>
                                                        <div className="text-xs text-muted mt-1">
                                                            {pl.visit_count} visit{pl.visit_count === 1 ? '' : 's'} ·
                                                            {' '}{Math.round(pl.total_dwell_minutes / 60)}h dwell ·
                                                            {' '}{Math.round((pl.confidence || 0) * 100)}% confidence ·
                                                            {' '}{pl.centroid_lat.toFixed(4)}, {pl.centroid_lon.toFixed(4)}
                                                        </div>
                                                    </div>
                                                    {canWrite && (
                                                        <select className="chip" value={pl.place_type}
                                                                disabled={busyPlace === pl.id}
                                                                onChange={e => classify(pl.id, e.target.value)}
                                                                title="Reclassify / confirm this place">
                                                            {AEA_PLACE_ORDER.map(t => <option key={t} value={t}>{AEA_PLACE[t].name}</option>)}
                                                        </select>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>}
                            </div>
                        </>
                    )}

                    {/* DSAR tracker — brokers holding the data (shown whenever a device is loaded) */}
                    {data && !data.locked && (
                        <div className="panel mb-6">
                            <div className="panel-header flex items-center justify-between">
                                <span className="font-display text-xl gold">Data brokers · DSAR tracker</span>
                                {canWrite && (
                                    <button className="chip" onClick={() => setDialog('dsar')}>＋ Log request</button>
                                )}
                            </div>
                            {(data.brokers || []).length === 0
                                ? <div className="px-6 py-8 text-muted text-sm">
                                    No DSAR requests logged yet. Use <b>Log DSAR request</b> to pick a broker, copy the
                                    letter and start the clock.
                                  </div>
                                : <div className="px-2 py-2">
                                    {data.brokers.map(b => {
                                        const answered = !!b.responded_at || b.outcome === 'data_returned' || b.outcome === 'no_data';
                                        const overdue = !answered && b.response_due_at && new Date(b.response_due_at) < new Date();
                                        const badgeText = answered ? (b.outcome || 'responded').replace(/_/g, ' ')
                                                        : overdue ? 'overdue' : 'awaiting';
                                        const badgeColor = b.outcome === 'data_returned' ? 'var(--red)'
                                                        : overdue ? 'var(--amber)' : 'var(--muted)';
                                        const badgeBg = b.outcome === 'data_returned' ? 'var(--red-bg)'
                                                     : overdue ? 'var(--amber-bg)' : 'var(--zone-1)';
                                        return (
                                            <div key={b.id} className="table-row px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                                                <div>
                                                    <div className="text-sm" style={{ color: 'var(--text)' }}>{b.broker_name}</div>
                                                    <div className="text-xs text-muted mt-1">
                                                        {(b.request_type || '').replace(/_/g, ' ')}
                                                        {b.sent_at && <> · sent {new Date(b.sent_at).toLocaleDateString()}</>}
                                                        {!answered && b.response_due_at && <> · due {new Date(b.response_due_at).toLocaleDateString()}</>}
                                                        {answered && b.responded_at && <> · responded {new Date(b.responded_at).toLocaleDateString()}</>}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono text-xs px-2 py-1 uppercase" style={{
                                                        borderRadius: 'var(--radius-pill)', background: badgeBg,
                                                        color: badgeColor, border: '1px solid var(--line)',
                                                    }}>{badgeText}</span>
                                                    {canWrite && !answered && (
                                                        <button className="chip" onClick={() => setIngestReq(b)}
                                                                title="Upload the data this broker returned">
                                                            Upload response
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                  </div>}
                        </div>
                    )}
                    {dialogs}
                </div>
            );
        }

export { ExposurePanel };
